// db-sidebar-filter.js

// ── Reviews (sidebar) ───────────────────────────────────────────────
const SIDEBAR_REVIEW_BUCKET_ORDER = ["4.5★+", "4★+", "3.5★+", "3★+", "<3★", "No reviews"];

// Threshold-based test for the Reviews sidebar selection
function passesReviewBuckets(cell, selectedSet) {
  const wrap  = cell?.querySelector('.td-wrapper');
  const avg   = parseFloat(wrap?.dataset.value || "0") || 0;
  const count = parseInt(wrap?.dataset.count || "0", 10) || 0;

  // special cases first
  if (selectedSet.has("No reviews")) {
    if (count === 0) return true;
  }
  if (selectedSet.has("<3★")) {
    if (count > 0 && avg < 3.0) return true;
  }

  // threshold selections (OR semantics)
  const thresholds = [
    ["4.5★+", 4.5],
    ["4★+",   4.0],
    ["3.5★+", 3.5],
    ["3★+",   3.0],
  ];
  for (const [label, min] of thresholds) {
    if (selectedSet.has(label) && count > 0 && avg >= min) return true;
  }
  return false;
}

// Debounce (reuse global if present)
const __debounce = window.debounce || ((fn, wait = 120) => {
  let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); };
});

// Reads the already-painted table cell and returns the bucket string.
// Mirrors the main filter’s logic + hydrator metadata. :contentReference[oaicite:0]{index=0}
function getSidebarReviewBucketFromCell(cell) {
  const wrap = cell?.querySelector?.('.td-wrapper');
  if (!wrap) return "No reviews";
  // Prefer hydrator’s metadata if present
  const bucket = wrap.dataset?.filter;
  if (bucket) return bucket;

  // Fallback to thresholds (same as main filter) if a cell isn't hydrated yet
  const avg   = parseFloat(wrap.dataset.value || "0") || 0;
  const count = parseInt(wrap.dataset.count || "0", 10) || 0;
  return (count === 0) ? "No reviews"
       : (avg >= 4.5) ? "4.5★+"
       : (avg >= 4.0) ? "4★+"
       : (avg >= 3.5) ? "3.5★+"
       : (avg >= 3.0) ? "3★+"
       : "<3★";
}

function injectSidebarFilterUI() {
  const sidebar = document.createElement("div");
  sidebar.id = "sidebarFilterPanel";
  sidebar.style.cssText = `
    position: absolute;
    left: 0;
    width: 300px;
    height: 100%;
    background: #fff;
    box-shadow: 2px 0 10px rgba(0,0,0,0.2);
    z-index: 9000;
    padding: 20px;
    overflow-y: auto;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  `;

  sidebar.innerHTML = `
     <div id="sidebarFiltersContent"></div>
  `;

  const fullscreenTarget = document.getElementById("tableContainer");
  (fullscreenTarget || document.body).appendChild(sidebar);

  const updateSidebarPosition = () => {
    const scrollTop = window.scrollY;
    const header = document.querySelector("#shopify-section-header");
    const textWithIcons = document.querySelector(
      "#shopify-section-text-with-icons"
    );

    const headerHeight = header ? header.offsetHeight : 0;
    const textWithIconsBottom = textWithIcons
      ? textWithIcons.offsetTop + textWithIcons.offsetHeight
      : 0;

    if (scrollTop + headerHeight >= textWithIconsBottom) {
      sidebar.style.position = "fixed";
      sidebar.style.top = `${headerHeight}px`;
    } else {
      sidebar.style.position = "absolute";
      sidebar.style.top = `${textWithIconsBottom + 2}px`;
    }
  };

  window.addEventListener("scroll", updateSidebarPosition);
  window.addEventListener("resize", updateSidebarPosition);
  updateSidebarPosition();
  buildSidebarFilterContents();
}

function buildSidebarDateAddedFilter(containerBody, urlParams) {
  const allDates = Array.from(document.querySelectorAll("#productsTableBody tr"))
    .map((row) => new Date(row.dataset.created))
    .filter((d) => !isNaN(d));

  if (!allDates.length) {
    containerBody.textContent = "(No values found)";
    return;
  }

  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));

  const urlMin = urlParams.get("Date Added_min");
  const urlMax = urlParams.get("Date Added_max");

  const currentMin = urlMin || minDate.toISOString().slice(0, 10);
  const currentMax = urlMax || maxDate.toISOString().slice(0, 10);

  containerBody.innerHTML = `
    <div style="display:flex;gap:8px;margin-bottom:8px;">
      <input type="date" id="sidebar-min-Date-Added" 
             value="${currentMin}" 
             min="${minDate.toISOString().slice(0,10)}" 
             max="${maxDate.toISOString().slice(0,10)}">
      <span>to</span>
      <input type="date" id="sidebar-max-Date-Added" 
             value="${currentMax}" 
             min="${minDate.toISOString().slice(0,10)}" 
             max="${maxDate.toISOString().slice(0,10)}">
    </div>
  `;

  const minInput = containerBody.querySelector("#sidebar-min-Date-Added");
  const maxInput = containerBody.querySelector("#sidebar-max-Date-Added");

function apply() {
  const vMin = minInput.value;
  const vMax = maxInput.value;

  const params = new URLSearchParams(window.location.search);
  if (vMin) params.set("Date Added_min", vMin);
  if (vMax) params.set("Date Added_max", vMax);
  history.replaceState(null, "", `${window.location.pathname}?${params}`);

  applySidebarFilters();
  updateClearButtonVisibility("Date Added");
  updateGroupClearButtonVisibility();
  updateFilterButtonsVisibility();
  highlightFilteredColumns();
}
  minInput.addEventListener("change", apply);
  maxInput.addEventListener("change", apply);
}

function buildSidebarFilterContents() {
  const tableRows = document.querySelectorAll("#productsTableBody tr");

  const allHeaders = Array.from(document.querySelectorAll("thead th")).map(
    (th) => th.textContent.trim()
  );

  const container = document.getElementById("sidebarFiltersContent");
  if (!container) return;
  container.innerHTML = "";

  const allColumns = allHeaders
    .map((label) => ({
      label: label.replace(/▼|⇅|\s+/g, " ").trim(),
    }))
    .filter(
      (col) =>
        col.label &&
        col.label.length > 1 &&
        !["Compare", "View", "Match %", "Name", "Favorite"].includes(col.label)
    );

  const usedLabels = new Set();
  const urlParams = new URLSearchParams(window.location.search);

  const brandColumn = allColumns.find((c) => c.label === "Brand");

  if (brandColumn) {
    usedLabels.add("Brand");
    const groupWrapper = document.createElement("div");
    groupWrapper.className = "filter-group";

    const groupHeader = document.createElement("h3");
    groupHeader.className = "filter-group-header";
    groupHeader.innerHTML = `<span class="arrow">▼</span> Brand`;

    const clearBrandBtn = document.createElement("button");
    clearBrandBtn.textContent = "×";
    clearBrandBtn.className = "clear-group-btn";
    clearBrandBtn.type = "button";
    clearBrandBtn.style.marginLeft = "8px";
    clearBrandBtn.title = "Clear all Brand filters";
    clearBrandBtn.style.display = "none";
    groupHeader.appendChild(clearBrandBtn);

    clearBrandBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      clearSidebarFilter("Brand");
      applySidebarFilters();
      updateSidebarOptionsBasedOnActiveFilters();
      updateGroupClearButtonVisibility();
      updateFilterButtonsVisibility();
    });

    const groupBody = document.createElement("div");
    groupBody.className = "filter-group-body";
    groupHeader.addEventListener("click", (e) => {
      // ① if click came from the clear-button, bail out immediately
      if (e.target.closest(".clear-group-btn")) return;

      const isOpen = groupBody.classList.contains("open");
      const arrow = groupHeader.querySelector(".arrow");

      if (isOpen) {
        const startHeight = groupBody.scrollHeight;
        groupBody.style.height = `${startHeight}px`;

        void groupBody.offsetHeight;

        groupBody.style.height = "0px";
        groupBody.classList.remove("open");
        if (arrow) arrow.textContent = "▼";
      } else {
        groupBody.classList.add("open");
        const targetHeight = groupBody.scrollHeight + "px";
        groupBody.style.height = targetHeight;
        if (arrow) arrow.textContent = "▲";
      }
    });

    groupBody.addEventListener("transitionend", (e) => {
      if (e.propertyName !== "height") return;

      if (groupBody.classList.contains("open")) {
        groupBody.style.height = "auto";
      } else {
        groupBody.style.height = "0px";
      }
    });

    const wrapper = document.createElement("div");
    wrapper.className = "sidebar-filter-block";
    wrapper.innerHTML = `
      <div class="sidebar-filter-header" data-column="Brand"></div>
      <div class="sidebar-filter-body" data-column="Brand">
        <!-- 1) Keep “Select All” here, outside the scrolling list -->
        <div class="select-all-wrapper">
          <label class="sidebar-filter-option" style="font-weight: bold;">
            <input
              type="checkbox"
              class="sidebar-filter-checkbox sidebar-filter-select-all"
              data-column="Brand"
            >
            Select All
          </label>
        </div>
    
        <!-- 2) Only this section will scroll -->
        <div class="options-scroll-wrapper"></div>
      </div>
    `;

    const containerBody = wrapper.querySelector(".sidebar-filter-body");

    const searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.placeholder = "Search brands...";
    searchInput.className = "brand-search-input";
    containerBody.parentElement.insertBefore(searchInput, containerBody);

    const rawValues = getUniqueValuesForColumn("Brand");

    if (rawValues.length === 0) {
      containerBody.textContent = "(No values found)";
    } else {
      const selectedValues = urlParams.getAll("Brand");
      const checkboxElems = [];

      rawValues.forEach((value) => {
        const isChecked = selectedValues.includes(value);
        const labelElem = document.createElement("label");
        labelElem.className = "sidebar-filter-option";
        labelElem.innerHTML = `
      <input type="checkbox" class="sidebar-filter-checkbox"
        data-column="Brand" value="${value}" ${isChecked ? "checked" : ""}>
      ${value}
    `;
        const cb = labelElem.querySelector("input");
        checkboxElems.push(cb);
        containerBody
          .querySelector(".options-scroll-wrapper")
          .appendChild(labelElem);

      });

      // Now wire up “Select All” after checkboxElems is populated
      if (rawValues.length >= 6) {
        const selectAll = containerBody.querySelector(
          ".sidebar-filter-select-all"
        );
        setupSidebarSelectAll(selectAll, checkboxElems, "Brand");
      }

      searchInput.addEventListener("input", () => {
        const query = searchInput.value.trim().toLowerCase();
        const allOptionLabels = containerBody
          .querySelector(".options-scroll-wrapper")
          .querySelectorAll(".sidebar-filter-option");

        allOptionLabels.forEach((label) => {
          const text = label.textContent.trim().toLowerCase();
          label.style.display = text.includes(query) ? "" : "none";
        });
      });
    }

    groupBody.appendChild(wrapper);

    groupWrapper.appendChild(groupHeader);
    groupWrapper.appendChild(groupBody);
    container.appendChild(groupWrapper);
  }

  const filterGroups = {
    Reviews: ["Reviews"],
    Dimensions: [
      //  "Size",
      "Dims",
      "Length (mm)",
      "Width (mm)",
      "Height (mm)",
      "Thickness (mm)",
      "Area (cm²)",
    ],
    Performance: [
      "Surface",
      "Glide Type",
      "Firmness",
      "Texture",
      "Coating",
      "Static Friction",
      "Dynamic Friction",
    ],
    "Base & Build": ["Base", "Stitched", "Washable", "Packaged"],
    Style: ["Colour", "Graphics", "Lighting"],
    "Good to know": ["Game Type", "Stock", "Price", "Warranty", "Date Added"],
  };

  Object.entries(filterGroups).forEach(([groupName, labels]) => {
    const groupWrapper = document.createElement("div");
    groupWrapper.className = "filter-group";

    const groupHeader = document.createElement("h3");
    groupHeader.className = "filter-group-header";
    groupHeader.innerHTML = `<span class="arrow">▼</span> ${groupName}`;

    const clearGroupBtn = document.createElement("button");
    clearGroupBtn.textContent = "×";
    clearGroupBtn.className = "clear-group-btn";
    clearGroupBtn.type = "button";
    clearGroupBtn.style.display = "none";
    clearGroupBtn.style.marginLeft = "8px";
    clearGroupBtn.title = `Clear all ${groupName} filters`;
    groupHeader.appendChild(clearGroupBtn);

    clearGroupBtn.addEventListener("click", (e) => {
      e.stopPropagation();

      labels.forEach((label) => {
        document
          .querySelectorAll(`.sidebar-filter-checkbox[data-column="${label}"]`)
          .forEach((cb) => (cb.checked = false));

        const selectAll = document.querySelector(
          `.sidebar-filter-select-all[data-column="${label}"]`
        );
        if (selectAll) {
          selectAll.checked = false;
          selectAll.dispatchEvent(new Event("change", { bubbles: true }));
        }

        const minInput = document.getElementById(`sidebar-min-${label}`);
        const maxInput = document.getElementById(`sidebar-max-${label}`);
        if (minInput && maxInput) {
          const defMin = parseFloat(minInput.min);
          const defMax = parseFloat(maxInput.max);
          minInput.value = defMin;
          maxInput.value = defMax;
          const sliderEl = document.getElementById(
            `sidebar-slider-${label.replace(/\s+/g, "-")}`
          );
          if (sliderEl?.noUiSlider) {
            sliderEl.noUiSlider.set([defMin, defMax], false);
          }
        }
      });

      // Special case: reset Date Added date inputs
      const dateMin = document.getElementById("sidebar-min-Date-Added");
      const dateMax = document.getElementById("sidebar-max-Date-Added");
      if (dateMin && dateMax) {
        dateMin.value = dateMin.min;
        dateMax.value = dateMax.max;
      }

      const params = new URLSearchParams(window.location.search);
      labels.forEach((label) => {
        params.delete(label);
        params.delete(`${label}_min`);
        params.delete(`${label}_max`);
      });
      history.replaceState(
        null,
        "",
        `${window.location.pathname}?${params.toString()}`
      );

      labels.forEach((label) => {
        document
          .querySelectorAll(`.filter-checkbox[data-column="${label}"]`)
          .forEach((cb) => (cb.checked = false));
        const pill = document.querySelector(
          `.selected-filter[data-column="${label}"]`
        );
        if (pill) pill.textContent = "";
      });

      applySidebarFilters();
      updateSidebarOptionsBasedOnActiveFilters();
      updateGroupClearButtonVisibility();

      updateFilterButtonsVisibility();
      highlightFilteredColumns();
    });

    const groupBody = document.createElement("div");
    groupBody.className = "filter-group-body";
    groupHeader.addEventListener("click", (e) => {
      // ① if click came from the clear-button, bail out immediately
      if (e.target.closest(".clear-group-btn")) return;
      
      const isOpen = groupBody.classList.contains("open");
      const arrow = groupHeader.querySelector(".arrow");

      if (isOpen) {
        const startHeight = groupBody.scrollHeight;
        groupBody.style.height = `${startHeight}px`;

        void groupBody.offsetHeight;

        groupBody.style.height = "0px";
        groupBody.classList.remove("open");
        if (arrow) arrow.textContent = "▼";
      } else {
        groupBody.classList.add("open");
        const targetHeight = groupBody.scrollHeight + "px";
        groupBody.style.height = targetHeight;
        if (arrow) arrow.textContent = "▲";
      }
    });

    groupBody.addEventListener("transitionend", (e) => {
      if (e.propertyName !== "height") return;

      if (groupBody.classList.contains("open")) {
        groupBody.style.height = "auto";
      } else {
        groupBody.style.height = "0px";
      }
    });

    labels.forEach((label) => {
      const column = allColumns.find((c) => c.label === label);
      if (!column) return;
      usedLabels.add(label);

      let values;
      if (column.label === "Game Type") {
        const raw = getUniqueValuesForColumn(column.label);
        const parts = raw.flatMap((v) =>
          v
            .split(/\s*(?:·|•|,|\/)\s*/)
            .map((x) => x.trim())
            .filter(Boolean)
        );
        values = Array.from(new Set(parts)).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      } else {
        values = getUniqueValuesForColumn(column.label).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      }
      // ✅ Make sidebar Colour values match dropdown (single colours, not combos)
      if (column.label === "Colour") {
        const parts = values.flatMap((v) =>
          String(v)
            .split(/\s*(?:·|•|,|\/)\s*/)
            .map((s) => s.trim())
            .filter(Boolean)
        );
        values = Array.from(new Set(parts)).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      }
      const includeSelectAll = values.length >= 6;
      
      const wrapper = document.createElement("div");
      wrapper.className = "sidebar-filter-block";    
      wrapper.innerHTML = `
        <div class="sidebar-filter-header" data-column="${column.label}">
          <h4 class="sidebar-filter-name">${column.label}</h4>
        </div>
        <div class="sidebar-filter-body" data-column="${column.label}">
          ${includeSelectAll
            ? `<div class="select-all-wrapper">
                 <label class="sidebar-filter-option" style="font-weight: bold;">
                   <input type="checkbox"
                          class="sidebar-filter-checkbox sidebar-filter-select-all"
                          data-column="${column.label}" />
                   Select All
                 </label>
               </div>`
            : ""
          }
          <div class="options-scroll-wrapper"></div>
        </div>
      `;

      const headerRow = wrapper.querySelector(".sidebar-filter-header");
      const containerBody = wrapper.querySelector(".sidebar-filter-body");

      if (column.label === "Game Type") {
        const parts = values.flatMap((v) =>
          v
            .split(/\s*(?:·|•|,|\/)\s*/)
            .map((x) => x.trim())
            .filter(Boolean)
        );
        values = Array.from(new Set(parts)).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      } else if (column.label === "Size") {
        const sizeOrder = ["3XL", "Extended", "XXL", "XL", "L", "M", "S"];
        values.sort((a, b) => {
          const ai = sizeOrder.indexOf(a),
            bi = sizeOrder.indexOf(b);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai === -1 && bi !== -1) return 1;
          if (bi === -1 && ai !== -1) return -1;
          return a.localeCompare(b, undefined, { sensitivity: "base" });
        });
      } else if (column.label === "Firmness") {
        const firmnessOrder = ["Extra Soft", "Soft", "Mid", "Hard"];
        values.sort((a, b) => {
          const ai = firmnessOrder.indexOf(a),
            bi = firmnessOrder.indexOf(b);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai === -1 && bi !== -1) return 1;
          if (bi === -1 && ai !== -1) return -1;
          return a.localeCompare(b, undefined, { sensitivity: "base" });
        });
      } else if (column.label === "Texture") {
        const textureOrder = [
          "Ultra-smooth",
          "Smooth",
          "Slightly Grainy",
          "Grainy",
          "Rough",
        ];
        values.sort((a, b) => {
          const ai = textureOrder.indexOf(a),
            bi = textureOrder.indexOf(b);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai === -1 && bi !== -1) return 1;
          if (bi === -1 && ai !== -1) return -1;
          return a.localeCompare(b, undefined, { sensitivity: "base" });
        });
      } else if (
        column.label === "Static Friction" ||
        column.label === "Dynamic Friction"
      ) {
        const frictionOrder = [
          "Very Low",
          "Low",
          "Moderately Low",
          "Moderate",
          "Moderately High",
          "High",
        ];
        values.sort((a, b) => {
          const ai = frictionOrder.indexOf(a),
            bi = frictionOrder.indexOf(b);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai === -1 && bi !== -1) return 1;
          if (bi === -1 && ai !== -1) return -1;
          return a.localeCompare(b);
        });
      } else {
        values.sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      }
      if (values.length === 0) {
        containerBody.textContent = "(No values found)";
        return;
      }

      const isRangeColumn = [
        "Price",
        "Length (mm)",
        "Width (mm)",
        "Height (mm)",
        "Area (cm²)",
      ].includes(column.label);
      // special case for Date Added (date range, not numeric)
      if (column.label === "Date Added") {
        buildSidebarDateAddedFilter(containerBody, urlParams);
        groupBody.appendChild(wrapper);
        return;
      }
      let minVal, maxVal;

      if (isRangeColumn) {
        if (column.label === "Area (cm²)") {
          values = [];

          allProducts.forEach((p) => {
            const tag = p.tags.find((t) => t.startsWith("Dims__"));
            if (!tag) return;
            const parts = tag.replace("Dims__", "").replace(/mm/gi, "").split(/[x×]/);
            const [length, width] = parts.map((v) => parseFloat(v.trim()));
            if (!isNaN(length) && !isNaN(width)) {
              const area = Math.round((length * width) / 100); // cm²
              values.push(area.toString());
            }
          });
        }
        if (values.length === 0) {
          containerBody.textContent = "(No values found)";
          return;
        }
        const colValues = values
          .map((v) => parseFloat(v.replace(/[^\d.]/g, "")))
          .filter((v) => !isNaN(v));

        if (colValues.length === 0) {
          containerBody.textContent = "(No values found)";
          return;
        }

        minVal = Math.min(...colValues);
        maxVal = Math.max(...colValues);
        const urlMin = parseFloat(urlParams.get(`${column.label}_min`));
        const urlMax = parseFloat(urlParams.get(`${column.label}_max`));

        const currentMin = !isNaN(urlMin) ? urlMin : minVal;
        const currentMax = !isNaN(urlMax) ? urlMax : maxVal;

        const sliderId = `sidebar-slider-${column.label.replace(/\s+/g, "-")}`;
        const minInputId = `sidebar-min-${column.label}`;
        const maxInputId = `sidebar-max-${column.label}`;

        containerBody.innerHTML = `
      <div style="display: flex; gap: 10px; margin-bottom: 8px;">
        <input type="number" step="1" id="${minInputId}" value="${currentMin}" min="${minVal}" max="${maxVal}" style="width: 60px;">
        <span>-</span>
        <input type="number" step="1" id="${maxInputId}" value="${currentMax}" min="${minVal}" max="${maxVal}" style="width: 60px;">
      </div>
      <div id="${sliderId}" style="margin: 12px 0;"></div>
    `;

        setTimeout(() => {
          const sliderEl = document.getElementById(sliderId);
          const minInput = document.getElementById(minInputId);
          const maxInput = document.getElementById(maxInputId);
          if (!sliderEl) return;

          noUiSlider.create(sliderEl, {
            start: [minVal, maxVal],
            connect: true,
            range: { min: minVal, max: maxVal },
            step: 1,
          });

          sliderEl.noUiSlider.on("update", (values) => {
            minInput.value = values[0];
            maxInput.value = values[1];
          });

          sliderEl.noUiSlider.on("set", () => {
            applySidebarRangeFilter(column.label);
            highlightFilteredColumns();
          });

          minInput.addEventListener("change", () => {
            sliderEl.noUiSlider.set([minInput.value, null]);
          });
          maxInput.addEventListener("change", () => {
            sliderEl.noUiSlider.set([null, maxInput.value]);
          });
        }, 0);

        groupBody.appendChild(wrapper);
        return;
      }

      const selectedValues = urlParams.getAll(column.label);
      const checkboxElems = [];

      values.forEach((value) => {
        const labelElem = document.createElement("label");
        labelElem.className = "sidebar-filter-option";
        const isChecked = selectedValues.includes(value);

        if (column.label === "Colour") {
          const parts = value
          .split(/\s*•\s*|\s*,\s*|\s*\/\s*/)
          .map((v) => v.trim())
          .filter(Boolean);
        
        const cssParts = parts.map(
          (label) => colourMap[label] || label.replace(/\s+/g, "").toLowerCase()
        );
        
        const swatchHtml = cssParts
          .map(
            (c) => `<div style="flex:1; background:${c};"></div>`
          )
          .join("");

          labelElem.innerHTML = `
      <input type="checkbox" class="sidebar-filter-checkbox"
             data-column="Colour" value="${value}" ${
            isChecked ? "checked" : ""
          }>
      <div class="colour-block sidebar-colour-swatch">${swatchHtml}</div>
      <span style="margin-left:6px">${value}</span>
    `;
        } else {
          labelElem.innerHTML = `
      <input type="checkbox" class="sidebar-filter-checkbox"
             data-column="${column.label}" value="${value}" ${
            isChecked ? "checked" : ""
          }>
      ${value}
    `;
        }

        const cb = labelElem.querySelector("input");
        checkboxElems.push(cb);
        const optionsWrapper = containerBody.querySelector(".options-scroll-wrapper");
          if (optionsWrapper) {
            optionsWrapper.appendChild(labelElem);
          }
      });

      const selectAll = containerBody.querySelector(".sidebar-filter-select-all");
      setupSidebarSelectAll(selectAll, checkboxElems, column.label);

      groupBody.appendChild(wrapper);
    });

    groupWrapper.appendChild(groupHeader);
    groupWrapper.appendChild(groupBody);
    container.appendChild(groupWrapper);
  });

  allColumns.forEach((column) => {
    if (usedLabels.has(column.label)) return;

    const groupWrapper = document.createElement("div");
    groupWrapper.className = "filter-group";

    const groupHeader = document.createElement("h3");
    groupHeader.className = "filter-group-header";
    groupHeader.innerHTML = `<span class="arrow">▼</span> ${column.label}`;

    const groupBody = document.createElement("div");
    groupBody.className = "filter-group-body";

    groupHeader.addEventListener("click", () => {
      const isVisible = groupBody.style.display === "block";
      groupBody.style.display = isVisible ? "none" : "block";
      const arrow = groupHeader.querySelector(".arrow");
      if (arrow) arrow.textContent = isVisible ? "▼" : "▲";
    });

    const wrapper = document.createElement("div");
    wrapper.className = "sidebar-filter-block";
wrapper.innerHTML = `
  <div class="sidebar-filter-header" data-column="${column.label}">
    <h4 class="sidebar-filter-name">${column.label}</h4>
  </div>
  <div class="sidebar-filter-body" data-column="${column.label}">
    <div class="options-scroll-wrapper"></div>
  </div>
`;
    const headerRow = wrapper.querySelector(".sidebar-filter-header");

    groupBody.appendChild(wrapper);

    const containerBody = wrapper.querySelector(".sidebar-filter-body");
    let values;
    if (column.label === "Game Type") {
      const raw = getUniqueValuesForColumn(column.label);
      const parts = raw.flatMap((v) =>
        v
          .split(/\s*(?:·|•|,|\/)\s*/)
          .map((x) => x.trim())
          .filter(Boolean)
      );
      values = Array.from(new Set(parts)).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );
    } else {
      values = getUniqueValuesForColumn(column.label).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );
    }
    if (values.length === 0) {
      containerBody.textContent = "(No values found)";
      return;
    }
    values.forEach((value, index) => {
      const labelElem = document.createElement("label");
      labelElem.className = "sidebar-filter-option";
      const isChecked = urlParams.getAll(column.label).includes(value);
      labelElem.innerHTML = `
        <input type="checkbox" class="sidebar-filter-checkbox" data-column="${
          column.label
        }" value="${value}" ${isChecked ? "checked" : ""}> ${value}
      `;
      const optionsWrapper = containerBody.querySelector(".options-scroll-wrapper");
      if (optionsWrapper) {
        optionsWrapper.appendChild(labelElem);
      }
    });
    if (usedLabels.has(column.label) || column.label === "Reviews") return;
    groupWrapper.appendChild(groupHeader);
    groupWrapper.appendChild(groupBody);
    container.appendChild(groupWrapper);
  });

  bindSidebarCheckboxEvents();
  applySidebarFilters();
  allColumns.forEach((col) => updateCheckboxClearButtonVisibility(col.label));
  updateGroupClearButtonVisibility();
  rebuildReviewsSidebarOptions();
}

function updateSidebarOptionsBasedOnActiveFilters() {
  const selectedFilters = {};
  document
    .querySelectorAll(".sidebar-filter-checkbox:checked")
    .forEach((cb) => {
      const col = cb.dataset.column;
      if (col === "Reviews") return; // <- Reviews never constrains the other option lists
      if (!selectedFilters[col]) selectedFilters[col] = new Set();
      selectedFilters[col].add(cb.value);
    });

  if (Object.keys(selectedFilters).length === 0) {
    document.querySelectorAll(".sidebar-filter-option").forEach((label) => {
      const input = label.querySelector("input[type='checkbox']");
      if (input.classList.contains("sidebar-filter-select-all")) return;
      label.style.opacity = "1";
      label.style.pointerEvents = "auto";
      if (input) input.disabled = false;
    });
    return;
  }

  const allProductsOfType = allProducts.filter(
    (p) => p.productType === currentProductType
  );
  const tagFieldsForType = getTagFieldsForType(currentProductType);
  const processedTagFields = tagFieldsForType.flatMap((f) => {
    if (f.label === "Dims") {
      const isMousepad = currentProductType === "Mousepads";
      return [
        { label: "Length (mm)", prefix: f.prefix, part: 0 },
        { label: "Width (mm)", prefix: f.prefix, part: 1 },
        {
          label: isMousepad ? "Thickness (mm)" : "Height (mm)",
          prefix: f.prefix,
          part: 2,
        },
      ];
    }
    return f;
  });

  const allColumns = allHeaders.filter(
    (h) => !["Compare", "View", "Match %", "Name", "Favorite"].includes(h.label)
  );

  allColumns.forEach((col) => {
    const colName = col.label;
    const container = document.querySelector(
      `.sidebar-filter-body[data-column="${colName}"]`
    );
    if (!container) return;

    if (colName === "Brand") {
      const temp = { ...selectedFilters };
      delete temp.Brand;
      const filtered = allProductsOfType.filter((p) =>
        Object.entries(temp).every(([c, set]) =>
          c === "Brand"
            ? set.has(p.vendor)
            : Array.from(set).some((val) =>
                p.tags.some((tag) =>
                  tag.toLowerCase().includes(val.toLowerCase())
                )
              )
        )
      );
      const avail = new Set(filtered.map((p) => getDisplayVendor(p.vendor)));
      container.querySelectorAll(".sidebar-filter-option").forEach((label) => {
        const input = label.querySelector("input[type='checkbox']");
        if (!input) return;

        if (input.classList.contains("sidebar-filter-select-all")) {
          label.style.opacity = "1";
          label.style.pointerEvents = "auto";
          input.disabled = false;
          return;
        }

        const txt = label.textContent.trim();
        const ok = avail.has(txt) || input.checked;
        label.style.opacity = ok ? "1" : "0.4";
        label.style.pointerEvents = ok ? "auto" : "none";
        input.disabled = !ok;
      });

      return;
    }

    if (colName === "Game Type") {
      const temp = { ...selectedFilters };
      delete temp[colName];
      const filtered = allProductsOfType.filter((p) =>
        Object.entries(temp).every(([c, set]) =>
          c === "Brand"
            ? set.has(p.vendor)
            : Array.from(set).some((val) =>
                p.tags.some((t) => t.toLowerCase().includes(val.toLowerCase()))
              )
        )
      );
      const avail = new Set();
      filtered.forEach((p) =>
        p.tags.forEach((t) => {
          if (!t.startsWith("Game Type__")) return;
          t.replace("Game Type__", "")
            .split(/\s*(?:·|•|,|\/)\s*/)
            .map((x) => x.trim())
            .filter(Boolean)
            .forEach((part) => avail.add(part));
        })
      );
      container.querySelectorAll(".sidebar-filter-option").forEach((label) => {
        const input = label.querySelector("input[type='checkbox']");
        if (!input) return;

        if (input.classList.contains("sidebar-filter-select-all")) {
          label.style.opacity = "1";
          label.style.pointerEvents = "auto";
          input.disabled = false;
          return;
        }

        const txt = label.textContent.trim();
        const ok = avail.has(txt) || input.checked;
        label.style.opacity = ok ? "1" : "0.4";
        label.style.pointerEvents = ok ? "auto" : "none";
        input.disabled = !ok;
      });

      return;
    }

    if (
      ["Price", "Length (mm)", "Width (mm)", "Height (mm)"].includes(colName)
    ) {
      return;
    }

    const fieldObj = processedTagFields.find((f) => f.label === colName);
    if (!fieldObj) return;
    const { prefix, part } = fieldObj;

    const temp = { ...selectedFilters };
    delete temp[colName];

    const filtered = allProductsOfType.filter((p) =>
      Object.entries(temp).every(([otherCol, set]) => {
        if (otherCol === "Brand") {
          return set.has(p.vendor);
        }
        if (otherCol === "Game Type") {
          const parts = p.tags
            .filter((t) => t.startsWith("Game Type__"))
            .flatMap((t) =>
              t.replace("Game Type__", "").split(/\s*(?:·|•|,|\/)\s*/)
            )
            .map((s) => s.trim())
            .filter(Boolean);
          return Array.from(set).some((val) => parts.includes(val));
        }
        const otherField = processedTagFields.find((f) => f.label === otherCol);
        if (!otherField) return true;
        const otherPrefix = otherField.prefix;
        const otherPart = otherField.part;
        const vals = p.tags
          .filter((t) => t.startsWith(otherPrefix))
          .map((t) => {
            let v = t.replace(otherPrefix, "").trim();
            if (otherPart != null) {
              const parts = v
                .replace(/mm/gi, "")
                .split(/[x×]/)
                .map((s) => s.trim());
              v = parts[otherPart] || "";
            }
            if (otherCol === "Thickness (mm)" && !isNaN(+v)) {
              v = parseFloat(v).toFixed(1);
            }
            return v;
          });
        return Array.from(set).some((val) => vals.includes(val));
      })
    );

    const availableValues = new Set();
    filtered.forEach((p) =>
      p.tags
        .filter((t) => t.startsWith(prefix))
        .forEach((t) => {
          let v = t.replace(prefix, "").trim();
          if (part != null) {
            const parts = v
              .replace(/mm/gi, "")
              .split(/[x×]/)
              .map((s) => s.trim());
            v = parts[part] || "";
          }
          if (colName === "Thickness (mm)" && !isNaN(+v)) {
            v = parseFloat(v).toFixed(1);
          }
          availableValues.add(v);
        })
    );

    container.querySelectorAll(".sidebar-filter-option").forEach((label) => {
      const input = label.querySelector("input[type='checkbox']");
      if (!input) return;

      if (input.classList.contains("sidebar-filter-select-all")) {
        label.style.opacity = "1";
        label.style.pointerEvents = "auto";
        input.disabled = false;
        return;
      }

      const txt = label.textContent.trim();
      const ok = availableValues.has(txt) || input.checked;
      label.style.opacity = ok ? "1" : "0.4";
      label.style.pointerEvents = ok ? "auto" : "none";
      input.disabled = !ok;
    });
  });
}

function getUniqueValuesForColumn(columnName) {
  const rows = document.querySelectorAll("tbody tr");
  const values = new Set();
  const ths = document.querySelectorAll("thead th");

  const columnIndex = Array.from(ths).findIndex((th) => {
    const btn = th.querySelector(".dropdown-toggle");
    return (btn?.dataset.column === columnName || th.textContent.trim() === columnName);
  });
  if (columnIndex === -1) return [];

  rows.forEach((row) => {
    const cell = row.children[columnIndex];
    if (!cell) return;

    if (columnName === "Reviews") {
      values.add(getSidebarReviewBucketFromCell(cell));
    } else {
      const wrapper = cell.querySelector(".td-wrapper");
      const text = wrapper ? (wrapper.dataset.value || wrapper.textContent).trim()
                           : cell.textContent.trim();
      if (text) values.add(text);
    }
  });

  // Keep review buckets in a predictable order
  if (columnName === "Reviews") {
    return SIDEBAR_REVIEW_BUCKET_ORDER.filter((b) => values.has(b));
  }
  return Array.from(values).filter(Boolean);
}

function bindSidebarCheckboxEvents() {
  const root = document.getElementById("sidebarFiltersContent");
  if (!root || root.__bound) return;        // prevent re-binding on rebuilds
  root.__bound = true;

  root.addEventListener("change", (e) => {
    const cb = e.target.closest(".sidebar-filter-checkbox");
    if (!cb) return;

    // existing body from your per-checkbox handler:
    const column = cb.dataset.column;
    updateClearButtonVisibility(column);
    updateGroupClearButtonVisibility();

    const allValues = Array.from(
      root.querySelectorAll(`.sidebar-filter-checkbox[data-column="${column}"]:checked`)
    ).map((n) => n.value);
    const pill = document.querySelector(`.selected-filter[data-column="${column}"]`);
    if (pill) pill.textContent = allValues.join(", ");

    updateFilterButtonsVisibility?.();
    highlightFilteredColumns?.();

    updateSidebarURL();
    applySidebarFilters();
    updateSidebarOptionsBasedOnActiveFilters(column);

    updateGroupClearButtonVisibility();
    updateCheckboxClearButtonVisibility(column);
  });
}

function applySidebarFilters() {
  const rows = document.querySelectorAll("tbody tr");

  const activeFilters = {};
  document
    .querySelectorAll(".sidebar-filter-checkbox:checked")
    .forEach((cb) => {
      const col = cb.dataset.column;
      if (!activeFilters[col]) activeFilters[col] = new Set();
      activeFilters[col].add(cb.value);
    });

  const ths = document.querySelectorAll("thead th");
  const columnIndices = {};
  ths.forEach((th, i) => {
    const btn = th.querySelector(".dropdown-toggle");
    const label = btn?.dataset.column || th.textContent.trim();
    if (label) columnIndices[label] = i;
  });

  // Mark Date Added active if params present
  const minStr = new URLSearchParams(window.location.search).get("Date Added_min");
  const maxStr = new URLSearchParams(window.location.search).get("Date Added_max");
  if (minStr || maxStr) {
    activeFilters["Date Added"] = new Set();
  }

rows.forEach((row) => {
  let visible = true;

  for (const [column, values] of Object.entries(activeFilters)) {
    if (column === "Reviews") {
      const idx  = columnIndices["Reviews"];
      const cell = row.children[idx];
      if (!passesReviewBuckets(cell, values)) { visible = false; break; }
    } else if (column === "Date Added") {
      const minStr = new URLSearchParams(window.location.search).get("Date Added_min");
      const maxStr = new URLSearchParams(window.location.search).get("Date Added_max");

      const min = minStr ? new Date(minStr) : null;
      const max = maxStr ? new Date(maxStr) : null;

      const created = new Date(row.dataset.created);

      if ((min && created < new Date(min)) || (max && created > new Date(max))) {
        visible = false;
        break;
      }
      } else if (column === "Game Type") {
        const id = row.getAttribute("data-product-id");
        const p = productMap?.[id];
        const tags = Array.isArray(p?.tags) ? p.tags : [];

        const parts = tags
          .filter((t) => t.startsWith("Game Type__"))
          .flatMap((t) => t.replace("Game Type__", "").split(/\s*(?:·|•|,|\/)\s*/))
          .map((s) => s.trim())
          .filter(Boolean);

        if (!parts.some((part) => values.has(part))) { visible = false; break; }
      } else if (column === "Colour") {
      const idx  = columnIndices["Colour"];
      const cell = row.children[idx];

      const raw = (cell?.querySelector(".td-wrapper")?.dataset.value || cell?.textContent || "").trim();
      if (!raw || raw === "—") { visible = false; break; }

      const parts = raw
        .split(/\s*(?:·|•|,|\/)\s*/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);

      const match = Array.from(values).some((filterVal) => {
        // filterVal can be "Black / Red" etc — match if ANY selected part exists in the row parts
        const wanted = String(filterVal)
          .split(/\s*(?:·|•|,|\/)\s*/)
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);

        return wanted.some((w) => parts.includes(w));
      });

      if (!match) { visible = false; break; }
    } else if (column === "Stock") {
      const id = row.getAttribute("data-product-id");
      const p = productMap[id];
      const available   = p?.variants?.edges[0]?.node?.availableForSale;
      const stockStatus = available ? "In Stock" : "Out of Stock";
      if (!values.has(stockStatus)) { visible = false; break; }
    } else {
      const idx    = columnIndices[column];
      const cell   = row.children[idx];
      const wrapper = cell?.querySelector(".td-wrapper");
      const text   = (wrapper ? wrapper.textContent : cell?.textContent || "").trim();
      if (!values.has(text)) { visible = false; break; }
    }
  }

  if (visible && showingFavoritesOnly) {
    const id = row.getAttribute("data-product-id");
    if (!favoritedProducts.includes(id)) visible = false;
  }

  row.style.display = visible ? "" : "none";
});

 // ── after all rows have been shown/hidden, sync the active view ──
 if (typeof updateProductView === "function") {
   // keep the sidebar open for this update
   suppressSidebarClose = true;
   updateProductView();
   suppressSidebarClose = false;
   // then refresh the badge
  refreshProductCounter();
  rebuildReviewsSidebarOptionsDebounced?.();
 }
}

function clearSidebarFilter(columnLabel) {
  document
    .querySelectorAll(`.sidebar-filter-checkbox[data-column="${columnLabel}"]`)
    .forEach((cb) => {
      cb.checked = false;
    });
  const selectAllBox = document.querySelector(
    `.sidebar-filter-select-all[data-column="${columnLabel}"]`
  );
  if (selectAllBox) selectAllBox.checked = false;

  const params = new URLSearchParams(window.location.search);
  params.delete(columnLabel);
  history.replaceState(
    null,
    "",
    `${window.location.pathname}?${params.toString()}`
  );

  const mainFilterSpan = document.querySelector(
    `.selected-filter[data-column="${columnLabel}"]`
  );
  if (mainFilterSpan) mainFilterSpan.textContent = "";

  const mainCheckboxes = document.querySelectorAll(
    `.filter-checkbox[data-column="${columnLabel}"]`
  );
  mainCheckboxes.forEach((cb) => (cb.checked = false));

  const columnHeader = document.querySelector(
    `th .dropdown-toggle[data-column="${columnLabel}"]`
  );
  if (columnHeader) {
    const parent = columnHeader.closest(".flex-container-1");
    if (parent?.classList.contains("filter-active")) {
      parent.classList.remove("filter-active");
    }
  }

  applySidebarFilters();
  updateSidebarOptionsBasedOnActiveFilters();
  updateGroupClearButtonVisibility();
  updateFilterButtonsVisibility();
  // sync grid when user clears a filter
 // …after filters are applied…
 if (typeof updateProductView === "function") {
   // keep the sidebar open
   suppressSidebarClose = true;
   updateProductView();
   suppressSidebarClose = false;
 }
  if (typeof updateFilterButtonsVisibility === "function") {
    updateFilterButtonsVisibility();
  }
}

function updateSidebarURL() {
  const params = new URLSearchParams(window.location.search);

  const grouped = {};

  document
    .querySelectorAll(".sidebar-filter-checkbox:checked")
    .forEach((cb) => {
      const col = cb.dataset.column;
      const val = cb.value;
      if (!grouped[col]) grouped[col] = new Set();
      grouped[col].add(val);
    });

  Object.keys(grouped).forEach((col) => params.delete(col));

  Object.entries(grouped).forEach(([col, valSet]) => {
    if (col === "Game Type") {
      const joined = Array.from(valSet).join(" • ");
      params.append(col, joined);
    } else {
      // ✅ Colour should be multi-param like other columns now
      valSet.forEach((val) => params.append(col, val));
    }
  });

  const url = `${window.location.pathname}?${params.toString()}`;
  history.replaceState(null, "", url);
}

function bindSidebarToggleEvents() {
  const btn = document.getElementById("toggleSidebarFilterBtn");
  if (!btn) return;

  let isOpen = false;

  btn.addEventListener("click", () => {
    isOpen = window.sidebarIsOpen || false;
    const sidebar = document.getElementById("sidebarFilterPanel");
    const wrapper = document.getElementById("tableWrapper");
    if (!sidebar || !wrapper) return;

    const colPanel = document.getElementById("columnTogglePanel");
    if (colPanel) colPanel.style.transform = "translateX(100%)";
    window.sidebarIsOpen = false;

    // only clear padding when *not* in grid-view
    if (!window.isGridView && wrapper) {
      wrapper.style.paddingRight = "";
    }

    const settingsBtn = document.getElementById("editColumnsBtn");
    if (settingsBtn) {
      settingsBtn.classList.remove("active", "active-x");
      const icon = document.getElementById("settingsIcon");
      if (icon) {
        icon.innerHTML = `<img src="https://cdn.shopify.com/s/files/1/0102/8426/9615/files/settings.svg?v=1750358988" />`;
      }
    }
    document.getElementById("editColumnsBtn")?.classList.remove("active-x");

    isOpen = !isOpen;
    sidebar.style.transform = isOpen ? "translateX(0)" : "translateX(-100%)";
    window.sidebarIsOpen = isOpen;
    sidebar.style.left = "0";

    // *only* move the table-wrapper when not in grid-view
    if (!window.isGridView && wrapper) {
      wrapper.style.transform    = isOpen ? "translateX(300px)" : "";
      wrapper.style.paddingRight = isOpen ? "300px" : "";
    } else if (wrapper) {
      // make sure grid-view stays put
      wrapper.style.transform    = "";
      wrapper.style.paddingRight = "";
    }

    setTimeout(() => {
      if (isOpen) {
        btn.classList.add("active");
        btn.innerHTML = "&times;";
      } else {
        btn.classList.remove("active");
        btn.innerHTML = `<img src="https://cdn.shopify.com/s/files/1/0102/8426/9615/files/filter---filled-_24x24_1x_27c9eff6-a79d-417b-a447-5e0ce0d5fe2c.svg?v=1748030935" />`;
      }
    }, 100);
  });
}

function applySidebarRangeFilter(column) {
  const minInput = document.getElementById(`sidebar-min-${column}`);
  const maxInput = document.getElementById(`sidebar-max-${column}`);
  if (!minInput || !maxInput) return;
  const min = parseFloat(minInput.value),
    max = parseFloat(maxInput.value);

  const params = new URLSearchParams(window.location.search);
  params.set(`${column}_min`, min);
  params.set(`${column}_max`, max);
  history.replaceState(null, "", `${window.location.pathname}?${params}`);

  const span = document.querySelector(
    `.selected-filter[data-column="${column}"]`
  );
  if (span) span.textContent = `${min}-${max}`;

  document.querySelectorAll("tbody tr").forEach((row) => {
    const idx = getColumnIndex(column);
    const cell = row.children[idx];
    const val = parseFloat(cell?.textContent.replace(/[^\d.]/g, "")) || 0;
    row.style.display = !isNaN(val) && val >= min && val <= max ? "" : "none";
  });

  if (column === "Area (cm²)") {
  document.querySelectorAll("tbody tr").forEach((row) => {
    const id = row.getAttribute("data-product-id");
    const p = productMap[id];
    const dimsTag = p.tags.find((t) => t.startsWith("Dims__"));
    if (!dimsTag) {
      row.style.display = "none";
      return;
    }
    const dims = dimsTag
      .replace("Dims__", "")
      .replace(/mm/gi, "")
      .split(/[x×]/)
      .map((s) => parseFloat(s.trim()));
    const [length, width] = dims;
    const area = Math.round((length * width) / 100);
    row.style.display = area >= min && area <= max ? "" : "none";
  });
}

  updateClearButtonVisibility(column);
  updateGroupClearButtonVisibility();
  updateFilterButtonsVisibility();

  highlightFilteredColumns();
  // sync grid view after range change
 // …after filters are applied…
 if (typeof updateProductView === "function") {
   // keep the sidebar open
   suppressSidebarClose = true;
   updateProductView();
   suppressSidebarClose = false;
 }
}

function clearSidebarRangeFilter(column, minDefault, maxDefault) {
  const safeName = column.replace(/\s+/g, "-");
  const minInput = document.getElementById(`sidebar-min-${column}`);
  const maxInput = document.getElementById(`sidebar-max-${column}`);
  const slider = document.getElementById(`sidebar-slider-${safeName}`);

  if (minInput) minInput.value = minDefault;
  if (maxInput) maxInput.value = maxDefault;
  if (slider && slider.noUiSlider)
    slider.noUiSlider.set([minDefault, maxDefault]);

  const params = new URLSearchParams(window.location.search);
  params.delete(`${column}_min`);
  params.delete(`${column}_max`);
  history.replaceState(
    null,
    "",
    `${window.location.pathname}?${params.toString()}`
  );

  clearSidebarFilter(column);

  updateClearButtonVisibility(column);
  updateGroupClearButtonVisibility();
  // sync grid after clearing range
 // …after filters are applied…
 if (typeof updateProductView === "function") {
   // keep the sidebar open
   suppressSidebarClose = true;
   updateProductView();
   suppressSidebarClose = false;
 }
}

function updateClearButtonVisibility(columnLabel) {
  const header = document.querySelector(
    `.sidebar-filter-header[data-column="${columnLabel}"]`
  );
  if (!header) return;

  const clearBtn = header.querySelector("button.clear-filter-btn");
  if (!clearBtn) return;

  const anyChecked = Array.from(
    document.querySelectorAll(
      `.sidebar-filter-checkbox[data-column="${columnLabel}"]`
    )
  ).some((cb) => cb.checked);

  let rangeActive = false;
  const sliderEl = document.getElementById(
    `sidebar-slider-${columnLabel.replace(/\s+/g, "-")}`
  );
  if (sliderEl && sliderEl.noUiSlider) {
    const defaultMin = sliderEl.noUiSlider.options.range.min;
    const defaultMax = sliderEl.noUiSlider.options.range.max;
    const [curMin, curMax] = sliderEl.noUiSlider
      .get()
      .map((v) => parseFloat(v));
    rangeActive = curMin !== defaultMin || curMax !== defaultMax;
  }
  if (columnLabel === "Date Added") {
  const minInput = document.getElementById("sidebar-min-Date-Added");
  const maxInput = document.getElementById("sidebar-max-Date-Added");
  if (minInput && maxInput) {
    const defMin = minInput.min;
    const defMax = maxInput.max;
    const rangeActive = (minInput.value !== defMin) || (maxInput.value !== defMax);
    clearBtn.style.display = rangeActive ? "inline-block" : "none";
    return;
  }
}

  clearBtn.style.display = anyChecked || rangeActive ? "inline-block" : "none";
}

function updateCheckboxClearButtonVisibility(columnLabel) {
  const checkboxes = document.querySelectorAll(
    `.sidebar-filter-checkbox[data-column="${columnLabel}"]`
  );
  const clearButton = document.querySelector(
    `.sidebar-filter-header[data-column="${columnLabel}"] button`
  );

  if (!clearButton) return;

  const anyChecked = Array.from(checkboxes).some((cb) => cb.checked);
  clearButton.style.display = anyChecked ? "inline-block" : "none";
}

function clearAllFilters() {
  document
    .querySelectorAll(".sidebar-filter-checkbox, .sidebar-filter-select-all")
    .forEach((cb) => {
      if (!cb.disabled && cb.checked) {
        cb.checked = false;
        cb.dispatchEvent(new Event("change"));
      }
    });

  document.querySelectorAll("[id^='sidebar-slider-']").forEach((slider) => {
    const column = slider.id.replace("sidebar-slider-", "").replace(/-/g, " ");
    const minInput = document.getElementById(`sidebar-min-${column}`);
    const maxInput = document.getElementById(`sidebar-max-${column}`);
    if (minInput && maxInput) {
      const defMin = parseFloat(minInput.min);
      const defMax = parseFloat(maxInput.max);
      minInput.value = defMin;
      maxInput.value = defMax;
      if (slider.noUiSlider) {
        slider.noUiSlider.set([defMin, defMax], false);
      }
    }
  });

  // Special case: reset Date Added inputs + remove URL params
  const dateMin = document.getElementById("sidebar-min-Date-Added");
  const dateMax = document.getElementById("sidebar-max-Date-Added");
  if (dateMin && dateMax) {
    dateMin.value = dateMin.min;
    dateMax.value = dateMax.max;
  }

  const params = new URLSearchParams();
  params.delete("Date Added_min");
  params.delete("Date Added_max");

  history.replaceState(null, "", `${window.location.pathname}?${params}`);

  applySidebarFilters();
  updateSidebarOptionsBasedOnActiveFilters();
  updateGroupClearButtonVisibility();
  updateFilterButtonsVisibility();
  highlightFilteredColumns();
}

function getColumnIndex(columnLabel) {
  const ths = document.querySelectorAll("thead th");
  return Array.from(ths).findIndex((th) => {
    const btn = th.querySelector(".dropdown-toggle");
    return (
      btn?.dataset.column === columnLabel ||
      th.textContent.trim() === columnLabel
    );
  });
}

function updateGroupClearButtonVisibility() {
  Object.entries({
    Brand: ["Brand"],
    Reviews: ["Reviews"],
    Dimensions: [
      "Size",
      "Dims",
      "Length (mm)",
      "Width (mm)",
      "Height (mm)",
      "Thickness (mm)",
      "Area (cm²)",
    ],
    Performance: [
      "Glide Type",
      "Surface",
      "Texture",
      "Coating",
      "Static Friction",
      "Dynamic Friction",
      "Firmness",
    ],
    "Base & Build": ["Base", "Stitched", "Packaged", "Washable"],
    Style: ["Colour", "Graphics", "Lighting"],
    "Good to know": ["Game Type", "Stock", "Price", "Warranty", "Date Added"],
  }).forEach(([groupName, labels]) => {
    const header = Array.from(
      document.querySelectorAll(".filter-group-header")
    ).find((h) => h.textContent.includes(groupName));
    if (!header) return;
    const btn = header.querySelector("button.clear-group-btn");
    if (!btn) return;
    const active = labels.some((label) => {
      const cbActive = Array.from(
        document.querySelectorAll(
          `.sidebar-filter-checkbox[data-column="${label}"]`
        )
      ).some((cb) => cb.checked);
      const minInput = (label === "Date Added")
        ? document.getElementById("sidebar-min-Date-Added")
        : document.getElementById(`sidebar-min-${label}`);
      const maxInput = (label === "Date Added")
        ? document.getElementById("sidebar-max-Date-Added")
        : document.getElementById(`sidebar-max-${label}`);
      const rangeActive =
        minInput &&
        maxInput &&
        (parseFloat(minInput.value) !== parseFloat(minInput.min) ||
          parseFloat(maxInput.value) !== parseFloat(maxInput.max));
      return cbActive || rangeActive;
    });
    btn.style.display = active ? "inline-block" : "none";
  });
}

function setupSidebarSelectAll(selectAllEl, checkboxElems, columnLabel) {
  if (!selectAllEl) return;

  selectAllEl.addEventListener("change", (e) => {
    const checked = e.target.checked;

    checkboxElems.forEach((cb) => {
      cb.checked = checked;
      cb.dispatchEvent(new Event("input", { bubbles: true }));
    });

    requestAnimationFrame(() => {
      updateClearButtonVisibility(columnLabel);
      updateGroupClearButtonVisibility();
      updateFilterButtonsVisibility();

      // ✅ Manually update URL
      const params = new URLSearchParams(window.location.search);
      params.delete(columnLabel);
      if (checked) {
        checkboxElems.forEach((cb) => {
          params.append(columnLabel, cb.value);
        });
      }
      history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);

      applySidebarFilters();
      updateSidebarOptionsBasedOnActiveFilters(columnLabel);
      highlightFilteredColumns();
    });
  });

  const syncSelectAllState = () => {
    const allChecked = checkboxElems.every((cb) => cb.checked);
    selectAllEl.checked = allChecked;
  };

  syncSelectAllState();

  checkboxElems.forEach((cb) =>
    cb.addEventListener("change", syncSelectAllState)
  );
}

function rebuildReviewsSidebarOptions() {
  const body = document.querySelector('.sidebar-filter-body[data-column="Reviews"]');
  if (!body) return;
  const list = body.querySelector('.options-scroll-wrapper');
  if (!list) return;

  const urlParams = new URLSearchParams(window.location.search);
  const selected = new Set(urlParams.getAll("Reviews"));
  const buckets = getUniqueValuesForColumn("Reviews");

  list.innerHTML = buckets.length ? "" : `<div class="sidebar-filter-option" style="opacity:.7">(No values found)</div>`;
  buckets.forEach((value) => {
    const label = document.createElement("label");
    label.className = "sidebar-filter-option";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.className = "sidebar-filter-checkbox";
    input.dataset.column = "Reviews";
    input.value = value;
    input.checked = selected.has(value);

    label.appendChild(input);
    label.append(" ", value);
    list.appendChild(label);
  });

  bindSidebarCheckboxEvents();
  updateCheckboxClearButtonVisibility("Reviews");
  updateClearButtonVisibility("Reviews");
  updateGroupClearButtonVisibility();
}

const rebuildReviewsSidebarOptionsDebounced = __debounce(rebuildReviewsSidebarOptions, 120);
window.addEventListener('db:review-cell-painted', rebuildReviewsSidebarOptionsDebounced);
window.addEventListener('db:reviews-hydrated',     rebuildReviewsSidebarOptionsDebounced);
window.addEventListener('db:table-rendered',       rebuildReviewsSidebarOptionsDebounced);

window.injectSidebarFilterUI = injectSidebarFilterUI;
window.bindSidebarToggleEvents = bindSidebarToggleEvents;
window.buildSidebarFilterContents         = buildSidebarFilterContents;
window.updateGroupClearButtonVisibility   = updateGroupClearButtonVisibility;
window.updateCheckboxClearButtonVisibility = updateCheckboxClearButtonVisibility;